<template>
  <section>
    <div class="hero has-background-white is-small">
      <div class="hero-body">
        <div class="container">
          <div class="columns is-align-items-center">
            <section
              class="column is-two-fifths revenue_search"
              v-if="fileContent.length === 0"
            >
              <b-field :label="$t('SelectMonth')" horizontal type="is-blue">
                <b-datepicker       
                  @update:modelValue="datePickerChanged"
                  :month-names="$tm('MonthNames')"
                  v-model="selectedDate"
                  type="month"
                  :loading="shouldDisplayLoading"
                  :disabled="shouldDisplayLoading"
                  :max-date="maxRevenueDate"
                  :min-date="minRevenueDate"
                  icon-pack="fas"
                  icon="calendar-day"
                  :trap-focus="true"
                  :mobile-native="false"
                />
              </b-field>
              <b-field horizontal :label="$t('Search')" type="is-blue">
                <b-input
                  :disabled="shouldDisplayLoading"
                  :loading="shouldDisplayLoading"
                  v-model="searchText"
                />
              </b-field>
              <b-field horizontal>
                <b-checkbox
                  data-test-id="showShopsWithDataButton"
                  v-model="checkboxGroup"
                  :disabled="shouldDisplayLoading"
                  native-value="showShopsWithData"
                >
                  <span>{{ $t("ShowShopsWithData") }}</span>
                </b-checkbox>
              </b-field>
            </section>

            <div class="column is-one-fifth">
              <b-field>
                <b-button
                  data-test-id="submitButton"
                  expanded
                  :loading="shouldDisplayLoading"
                  class="is-primary submitButton has-text-red"
                  v-on:keyup.enter="sendRevenue"
                  @click="sendRevenue"
                  :disabled="submitButtonDisabled"
                  size="is-large"
                  >{{ $t("SendRevenueWithoutVat") }}</b-button
                >
              </b-field>
              <b-message size="is-small" type="is-warning" has-icon>{{
                $t("InsertRevenueWithoutVat")
              }}</b-message>
            </div>

            <div class="column is-hidden-touch is-invisible">
              <figure v-if="$t('CompaniesLogo')" class="image is-2by1">
                <img :src="$t('CompaniesLogo')" />
              </figure>
            </div>

            <div class="column is-hidden-touch revenue_controls">
              <FileInput
                @file-uploaded="fileUploaded"
                @error="fileError"
                :shopsList="shopList"
                :disabled="shouldDisplayLoading"
              />
              <FileExporter
                class="fileExporter"
                :disabled="shouldDisplayLoading"
                :content="flattenedShopList"
                :fileName="
                  'MallShopLinkSample' +
                    new Date().getDate() +
                    (new Date().getMonth() + 1) +
                    new Date().getFullYear() +
                    '.xlsx'
                "
              />
              <b-field>
                <b-button type="is-danger" @click="openHelpPage" expanded>
                  {{ $t("RevenueInputHelp") }}
                </b-button>
              </b-field>
            </div>
          </div>
        </div>
      </div>
    </div>
    <section v-if="fileContent.length === 0">
      <div class="container">
        <RevenueInputDetails
          ref="revenueInputs"
          class="revenueInput"
          :modelValue="shopList"
          :loading="shouldDisplayLoading"
          :filterPhrase="searchText"
          :utcSelectedDate="utcSelectedDate"
          :showDisabledShops="showDisabledShops"
          :showShopsWithData="showShopsWithData"
          @shop-list-filtered="onShopListFiltered"
        />
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import * as Sentry from '@sentry/browser'

import revenueService from '@/services/revenueService'
import FileInput from '@/components/RevenueFileInput.vue'
import FileExporter from '@/components/FileExporter.vue'
import shopsService from '@/services/shopsService'
import RevenueInputDetails from '@/views/RevenueInputDetails.vue'
import { useAppStore, type Shop } from '@/stores/app'

interface RevenueFileRow {
  ShopName: string
  ShoppingCenterName: string
  ChainShopId?: string | number | null
  SectionId: string | number
  RevenueInteger: string | number | null
  Year: number
  Month: number
}

interface RevenuePostItem {
  shopId: number
  sectionId: number
  revenueValue: number
}

interface RevenuePostPayload {
  revenues: RevenuePostItem[]
  revenueDate: Date
  revenueType: number
}

interface RevenueInputValidationResult {
  isValid: boolean
  shopName?: string
  validationMessage?: string
}

interface RevenueInputDetailsExposed {
  validateRevenueInput: () => RevenueInputValidationResult
}

interface RevenueValidationError {
  failureReason: string
  relatedFailureParameter?: string
  identifierDescription?: string
}

interface RevenuePostResponseRow {
  shopId: number
  sectionId: number
  updateSucceed: boolean
  relatedFailureParameter: string | number | null
  failureReason: string
}

interface RevenuePostResponse {
  status: number
  data: RevenuePostResponseRow[]
}

interface HttpError {
  response?: RevenuePostResponse
  config?: {
    data?: unknown
  }
}

interface BuefyNotificationOptions {
  message: string
  type: 'is-danger'
  hasIcon: boolean
  indefinite: boolean
  closable: boolean
  position: 'is-top'
}

interface BuefyProxy {
  $buefy?: {
    notification: {
      open: (options: BuefyNotificationOptions) => void
    }
  }
}

// Get current instance for accessing $buefy
const instance = getCurrentInstance()

// Composables
const router = useRouter()
const { t } = useI18n()
const appStore = useAppStore()

// Template refs
const revenueInputs = ref<RevenueInputDetailsExposed | null>(null)

// Reactive data
const selectedDate = ref<Date>(new Date(
  new Date().getFullYear(),
  new Date().getMonth() - 1,
  15
))
const searchText = ref<string>('')
const fileContent = ref<RevenueFileRow[]>([])
const checkboxGroup = ref<string[]>([])
const processing = ref<boolean>(false)
const filteredShopList = ref<Shop[] | null>(null)
let latestIndicationRequestId = 0
let lastRequestedYearMonth: string | null = null

// Computed properties
const shopList = computed<Shop[] | null>(() => appStore.getAllowedShopsList)

const submitButtonDisabled = computed(() => {
  if (fileContent.value.length > 0) return false
  if (!shopList.value) return true
  return shopList.value.every(shop => {
    return shop.sections.some(section => section.revenueValue?.toString()) === false
  })
})

const utcSelectedDate = computed(() => {
  return new Date(
    Date.UTC(
      selectedDate.value.getFullYear(),
      selectedDate.value.getMonth(),
      selectedDate.value.getDate()
    )
  )
})

const maxRevenueDate = computed(() => {
  const now = new Date()
  now.setMonth(now.getMonth() === 0 ? 11 : now.getMonth() - 1)
  return now
})

const minRevenueDate = computed(() => {
  const now = new Date()
  now.setFullYear(now.getFullYear() - 2, 0, 1)
  now.setHours(0, 0, 0, 0)
  return now
})

const shouldDisplayLoading = computed(() => {
  return !Array.isArray(shopList.value) || processing.value
})

const flattenedShopList = computed<ReturnType<typeof revenueService.prepareDataForFileSample> | null>(() => {
  if (filteredShopList.value && filteredShopList.value.length > 0) {
    const flattenedList = revenueService.prepareDataForFileSample(
      filteredShopList.value,
      utcSelectedDate.value
    )
    return flattenedList
  }
  return null
})

const showDisabledShops = computed(() => {
  return checkboxGroup.value.some(item => item === 'showDisabledShops')
})

const showShopsWithData = computed(() => {
  return checkboxGroup.value.some(item => item === 'showShopsWithData')
})

// Methods
const onShopListFiltered = (value: Shop[] | null): void => {
  filteredShopList.value = value
}

const mapFileRowToRevenue = (row: RevenueFileRow): RevenuePostItem => {
  const shopsCollection = (shopList.value || []) as Parameters<typeof shopsService.getShopFromCollection>[0]
  const shopToFind: Parameters<typeof shopsService.getShopFromCollection>[1] = {
    ShopName: row.ShopName,
    ShoppingCenterName: row.ShoppingCenterName,
    ChainShopId: row.ChainShopId ?? undefined,
    SectionId: row.SectionId
  }

  return {
    shopId: Number(shopsService.getShopFromCollection(shopsCollection, shopToFind)?.shopId || 0),
    sectionId: Number(row.SectionId),
    revenueValue: Number(row.RevenueInteger)
  }
}

const sendRevenue = async (): Promise<void> => {
  const inputComponent = revenueInputs.value
  if (inputComponent) {
    const inputValidation = inputComponent.validateRevenueInput()
    if (inputValidation.isValid === false) {
      openErrorNotification(
        `${inputValidation.shopName} ${inputValidation.validationMessage}`
      )
      return
    }
  }
  processing.value = true
  let data: RevenuePostPayload
  if (fileContent.value.length === 0) {
    data = {
      revenues: [],
      revenueDate: utcSelectedDate.value,
      revenueType: 2
    }
    if (shopList.value) {
      data.revenues = revenueService.prepareRevenueData(shopList.value) as RevenuePostItem[]
    }
  } else {
    const revenues = fileContent.value.map((row) => mapFileRowToRevenue(row))
    const firstFileRow = fileContent.value[0]
    data = {
      revenueDate: new Date(Date.UTC(firstFileRow.Year, firstFileRow.Month - 1, 1)),
      revenueType: 2,
      revenues
    }
  }

  try {
    const response = await revenueService.postRevenue(data)
    handlePostRevenueResponse(
      {
        status: response.status,
        data: (response.data ?? []) as RevenuePostResponseRow[]
      },
      undefined
    )
  } catch (err) {
    const httpError = err as HttpError
    handlePostRevenueResponse(httpError.response, httpError)
  }
}

const fileUploaded = (data: RevenueFileRow[]): void => {
  fileContent.value = data
}

const fileError = (errors: Array<RevenueValidationError | RevenueValidationError[]>): void => {
  errors
    .reduce<RevenueValidationError[]>(
      (conflicts, conflictItem) => conflicts.concat(conflictItem),
      []
    )
    .forEach((error) => {
      openErrorNotification(
        t(error.failureReason, {
          relatedFailureParameter: error.relatedFailureParameter,
          identifierDescription: error.identifierDescription
        })
      )
    })
}

const handlePostRevenueResponse = (response?: RevenuePostResponse, err?: HttpError): void => {
  if (response && (response.status < 300 || response.status === 409)) {
    const overrideInfo: Parameters<typeof shopsService.addDetailsToShopSection>[2] =
      fileContent.value.length === 0
        ? { length: 0, revenues: [] }
        : {
            length: fileContent.value.length,
            revenues: fileContent.value.map((row) => mapFileRowToRevenue(row))
          }

    const enrichedResponse = shopsService.addDetailsToShopSection(
      (shopList.value || []) as Parameters<typeof shopsService.addDetailsToShopSection>[0],
      response.data as Parameters<typeof shopsService.addDetailsToShopSection>[1],
      overrideInfo
    )

    router.push({
      name: "submitSummary",
      state: {
        shoppingCentersList: JSON.stringify(enrichedResponse),
        date: utcSelectedDate.value.toISOString()
      }
    })
  } else if (err) {
    openErrorNotification(t("ErrorContactUs") + err)
    Sentry.withScope(scope => {
      scope.setExtra("responseData", err.response?.data ?? err.response)
      scope.setExtra("requestData", err.config?.data ?? err.config)
      Sentry.captureException(err)
    })
  } else {
    if (response) {
      revenueService.postRevenueDone(
        response as unknown as Parameters<typeof revenueService.postRevenueDone>[0]
      )
    }
  }
  processing.value = false
}

const datePickerChanged = async (selected: Date | null = null) => {
  const effectiveDate = selected ?? selectedDate.value
  if (!effectiveDate) {
    return
  }

  const monthNumber = effectiveDate.getMonth() + 1
  const yearNumber = effectiveDate.getFullYear()
  const yearMonthKey = `${yearNumber}-${monthNumber}`
  if (yearMonthKey === lastRequestedYearMonth) {
    return
  }

  lastRequestedYearMonth = yearMonthKey
  selectedDate.value = effectiveDate

  const month = String(monthNumber)
  const year = String(yearNumber)
  const requestId = ++latestIndicationRequestId

  const [indicationResult, shopsResult] = await Promise.allSettled([
    revenueService.getRevenueIndication(month, year, {
      applyToStore: false
    }),
    shopsService.getAllowedShopsListResult(month, year)
  ])

  if (requestId !== latestIndicationRequestId) {
    return
  }

  let hasRequestFailure = false

  if (indicationResult.status === 'fulfilled') {
    if (indicationResult.value.success) {
      appStore.setRevenueIndication(indicationResult.value.data)
    } else {
      hasRequestFailure = true
    }
  } else {
    hasRequestFailure = true
  }

  if (shopsResult.status === 'fulfilled') {
    if (shopsResult.value.success) {
      appStore.setAllowedShopsList(shopsResult.value.data)
    } else {
      hasRequestFailure = true
    }
  } else {
    hasRequestFailure = true
  }

  if (hasRequestFailure) {
    openErrorNotification(t("ErrorContactUs"))
  }
}

const openErrorNotification = (message: string): void => {
  const componentProxy = instance?.proxy as BuefyProxy | null

  if (componentProxy?.$buefy) {
    componentProxy.$buefy.notification.open({
      message,
      type: "is-danger",
      hasIcon: true,
      indefinite: true,
      closable: true,
      position: "is-top"
    })
  }
}

const openHelpPage = () => {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line no-undef
    window.open("https://www.mallshoplink.com/help?lang=he", "_blank")
  }
}

// Lifecycle
onMounted(() => {
  if (typeof sessionStorage !== 'undefined') {
    const session = sessionStorage.getItem("dataFromToken")
    if (session) {
      selectedDate.value = new Date(
        parseInt(session.substring(0, 4), 10),
        Number(session.slice(4, 6)) - 1
      )
    }
    sessionStorage.removeItem("dataFromToken")
  }
  datePickerChanged()
})
</script>

<style lang="scss">
.hero {
  width: 100%;
}
.submitButton {
  white-space: pre-line;
  line-height: 1.2em;
}
.centeredButton {
  margin-right: 15%;
  margin-left: 25%;
  width: 100%;
}
.revenueInput {
  margin-bottom: 35px;
}
.fixed {
  position: fixed;
  top: calc(100vh - 3rem);
}
.fileExporter {
  margin-bottom: 0.6em;
}
.fileExporter .button > span:not(.icon) {
  display: flex;
}
.revenue_controls {
  .button.is-small {
    width: 100%;
  }
}
.revenue_search {
  .field.is-horizontal {
    justify-content: space-between;
    align-content: center;
    .field-label {
      margin: 0;
      text-align: inherit;
      padding-left: 0.6em;
      padding-right: 0.6em;
      flex-basis: 30%;
      flex-grow: 1;
    }
    .field-body {
      flex-basis: 70%;
      flex-grow: 1;
    }
    .b-checkbox.checkbox:not(.button) {
      margin: 0;
    }
    .b-checkbox.checkbox .control-label {
      padding: 0 0.25em;
    }
  }
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  /* display: none; <- Crashes Chrome on hover */
  -webkit-appearance: none;
  margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
}

input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield; /* Firefox */
}

@media screen and (min-width: 600px) {
  .half-width {
    width: 50%;
  }
}

.shopRevenueForm > * {
  width: 100%;
}

.clearfix {
  clear: both;
}
</style>
